import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AE_INPUT,
  AE_LABEL,
  AE_MUTED,
  AE_TEXTAREA,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeTemplateDetail } from '@/types/assessmentEngine';
import { useDebouncedSave } from './useDebouncedSave';
import { useTemplateSaveError } from './useTemplateSaveError';

interface TemplateMetadataEditorProps {
  template: AeTemplateDetail;
  /** Refresh page header and sibling tab after name/description save. */
  onReload: () => void;
}

/** Template display name and overview — editable in any status; lives on Setup tab only. */
export const TemplateMetadataEditor: React.FC<TemplateMetadataEditorProps> = ({ template, onReload }) => {
  const { t } = useLanguage();
  const onSaveError = useTemplateSaveError();
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  const [saving, setSaving] = useState(false);

  const serverRevision = useMemo(
    () => `${template.id}\0${template.name}\0${template.description ?? ''}`,
    [template.id, template.name, template.description]
  );

  useEffect(() => {
    setName(template.name);
    setDescription(template.description ?? '');
  }, [serverRevision, template.name, template.description]);

  const persist = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;
    setSaving(true);
    try {
      await assessmentEngineService.updateTemplate(template.id, {
        name: trimmedName,
        description: description.trim() || null,
      });
      onReload();
    } finally {
      setSaving(false);
    }
  }, [template.id, name, description, onReload]);

  useDebouncedSave(persist, [name, description], 800, { onError: onSaveError });

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-surface-text)]">
          {t('assessmentEngine.builder.templateSettings')}
        </h3>
      </div>
      <div>
        <label className={AE_LABEL} htmlFor="ae-template-name">
          {t('assessmentEngine.builder.templateName')}
        </label>
        <input
          id="ae-template-name"
          className={`${AE_INPUT} w-full`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div>
        <label className={AE_LABEL} htmlFor="ae-template-overview">
          {t('assessmentEngine.builder.templateOverview')}
        </label>
        <p className={`${AE_MUTED} text-xs mb-1`}>{t('assessmentEngine.builder.templateOverviewHint')}</p>
        <textarea
          id="ae-template-overview"
          className={`${AE_TEXTAREA} w-full min-h-[96px]`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      {saving && <p className={AE_MUTED}>{t('assessmentEngine.builder.saving')}</p>}
    </section>
  );
};
